import React from 'react';
import ReactDOM from 'react-dom';

import { Outlet, ReactLocation, Router } from 'react-location';

import { Home } from './pages/home';
import { NonHomeLayout } from './pages/page-boilerplate';
import { ExperienceSection } from './components/experience-section';
import { ContactSection } from './components/contact-section';
import ProjectsSection from './components/projectsSection';
import Project from './pages/project[projID]';

import './styles/main.scss';
import './styles/scroll.scss';

import { fetchProjectsFromGoogleDrive } from './services/googleDriveService';
import logger from './utils/logger';
import { ProjectsProvider } from './contexts/ProjectsContext';
import { FullscreenLoader } from './components/loading-spinner';

function App() {
  const [projects, setProjects] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  // Fetch project data on app initialization
  React.useEffect(() => {
    const initializeProjects = async () => {
      logger.log('🚀 Starting project initialization...');
      try {
        logger.log('📡 Fetching from Google Drive...');
        const googleDriveProjects = await fetchProjectsFromGoogleDrive();

        if (googleDriveProjects) {
          logger.log(
            '✅ Using projects from Google Drive:',
            Object.keys(googleDriveProjects).length,
            'projects'
          );
          setProjects(googleDriveProjects);
        } else {
          logger.error('❌ No projects data received from Google Drive');
          setProjects(null);
        }
      } catch (error) {
        logger.error('❌ Error initializing projects:', error);
        setProjects(null);
      } finally {
        logger.log('🏁 Project initialization complete');
        setLoading(false);
      }
    };

    initializeProjects();
  }, []);

  React.useEffect(() => {
    logger.log('projects', projects);
  }, [projects]);

  const reactLocation = new ReactLocation();

  // Determine loading spinner variant based on current route
  const getSpinnerVariant = () => {
    const path = window.location.pathname;
    if (path === '/') {
      return 'home';
    }
    return 'project';
  };

  // Show loading screen while projects are being fetched
  if (loading) {
    logger.log('⏳ App is in loading state');
    return <FullscreenLoader variant={getSpinnerVariant()} />;
  }

  // Don't render routes until projects are loaded
  if (!projects) {
    logger.log('❌ No projects data available');
    return (
      <FullscreenLoader
        message="Error loading portfolio data, please try again later."
        variant={getSpinnerVariant()}
      />
    );
  }

  logger.log(
    '🎉 App ready to render with',
    Object.keys(projects).length,
    'projects'
  );

  const routes = [
    {
      path: '/',
      element: <Home />,
    },
    {
      path: 'work',
      element: (
        <NonHomeLayout
          pageName={'work'}
          renderH1={'experience'}
          rowNum={2}
          renderChild={(animationFinished) => (
            <ExperienceSection {...{ animationFinished }} />
          )}
        />
      ),
    },
    {
      path: 'projects',
      children: [
        {
          path: '/', // matches /projects exactly
          element: (
            <NonHomeLayout
              pageName={'projects'}
              renderH1={'projects'}
              rowNum={3}
              renderChild={(animationFinished) => (
                <ProjectsSection {...{ animationFinished }} />
              )}
            />
          ),
        },
        {
          path: ':projID',
          element: <Project />,
          loader: async ({ params: { projID } }) => {
            return projects[projID];
          },
        },
      ],
    },
    {
      path: 'contact',
      element: (
        <NonHomeLayout
          pageName={'contact'}
          renderH1={'contact me'}
          rowNum={4}
          renderChild={(animationFinished) => (
            <ContactSection {...{ animationFinished }} />
          )}
        />
      ),
    },
    // fallback (404):
    {
      element: <Home />,
    },
  ];

  return (
    <ProjectsProvider projects={projects}>
      <Router location={reactLocation} routes={routes}>
        <Outlet></Outlet>
      </Router>
    </ProjectsProvider>
  );
}

ReactDOM.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
  document.getElementById('root')
);
